// Utilidades de escaneo de tickets (OCR gratuito con Tesseract.js, 100% en
// el navegador, sin ningún servicio de pago ni servidor propio) y de
// guardado de adjuntos (fotos de tickets u otros documentos) en Firebase
// Storage — esto último SÍ tiene un coste (mínimo) y es función Pro.
// Compartido entre Anadir.js (formulario completo) y Panel.js (acceso
// rápido desde el botón de escaneo del panel), para no duplicar la
// heurística de lectura ni la subida de archivos en dos sitios.
//
// Heurística sobre el texto que detecta el OCR: busca líneas que contengan
// "total" (evitando "subtotal", "cambio", "entregado", líneas del desglose
// de IVA, etc.), prioriza las que tienen pinta clara de ser EL total del
// ticket ("TOTAL (7 art.)", "TOTAL A PAGAR"...) y, si ninguna línea da la
// talla, usa como último recurso el importe más alto de la columna de la
// derecha de cada línea (no cualquier número del ticket — así una cantidad
// de producto como "2,000 kg" no se confunde con un importe). La
// descripción es la primera línea con letras de verdad (normalmente el
// nombre del comercio, que suele ir arriba del todo). Ninguno de los dos
// campos se guarda solo — quien escanea los ve ya puestos en el formulario
// y los puede corregir antes de guardar.

export function normalizeMoney(raw) {
  if (raw.includes(",")) return raw.replace(/\./g, "").replace(",", ".");
  const parts = raw.split(".");
  if (parts.length > 1 && parts[parts.length - 1].length === 2) {
    return parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
  }
  return raw;
}

export function parseReceiptText(text) {
  const lines = (text || "").split("\n").map(l => l.trim()).filter(Boolean);
  const moneyRegex = /\d{1,3}(?:[.,]\d{3})*[.,]\d{2}/g;
  const totalKeywords = /total/i;
  // Cualquier línea con un "%" se descarta como candidata: casi siempre es
  // una fila del desglose de IVA (4%, 10%, 21%...), no el total del ticket
  // (esto es lo que más suele confundir la lectura en supermercados como
  // Mercadona, que imprimen esa tabla justo debajo del total real).
  const excludeKeywords = /(subtotal|sub[\s-]?total|cambio|entregado|recibido|iva|base imponible|descuento|bonificaci[oó]n|%)/i;
  // Señales fuertes de que ESTA línea es el total de verdad (no un
  // desglose): "TOTAL (7 art.)", "TOTAL A PAGAR", "IMPORTE TOTAL", o la
  // línea empieza directamente por "TOTAL".
  const strongTotalKeywords = /(total\s*\(\d|total\s+a\s+pagar|importe\s+total|total\s+compra|total\s+ticket|^total\b)/i;

  const candidates = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!totalKeywords.test(line) || excludeKeywords.test(line)) continue;

    // El importe casi siempre está en la misma línea que "TOTAL", pero si
    // el ticket lo separa en dos líneas (columnas muy separadas, foto en
    // ángulo, salto de línea raro del OCR...), se mira también en la
    // siguiente línea antes de descartar esta candidata.
    let matches = line.match(moneyRegex);
    if ((!matches || !matches.length) && lines[i + 1]) {
      matches = lines[i + 1].match(moneyRegex);
    }
    if (!matches || !matches.length) continue;

    candidates.push({ raw: matches[matches.length - 1], strong: strongTotalKeywords.test(line) });
  }

  let totalRaw = null;
  if (candidates.length) {
    // Si hay alguna candidata "fuerte" se usa esa (la última, por si hay
    // más de una); si no, se usa la última candidata encontrada a secas.
    const strong = candidates.filter(c => c.strong);
    const pool = strong.length ? strong : candidates;
    totalRaw = pool[pool.length - 1].raw;
  }

  if (!totalRaw) {
    // Último recurso: en vez de mirar TODOS los números sueltos del ticket
    // (una cantidad de producto tipo "2,000 kg" puede leerse como importe
    // y liarlo todo), se coge de cada línea solo el ÚLTIMO número — la
    // columna de la derecha, que en cualquier línea de un ticket es su
    // importe — y se usa el más alto de todas las líneas.
    const lineTotals = lines
      .map(l => l.match(moneyRegex))
      .filter(m => m && m.length)
      .map(m => parseFloat(normalizeMoney(m[m.length - 1])))
      .filter(n => !isNaN(n));
    if (lineTotals.length) totalRaw = String(Math.max(...lineTotals));
  }

  const amount = totalRaw ? normalizeMoney(totalRaw) : "";
  const desc = lines.find(l => /[a-zA-ZÁÉÍÓÚÑÜáéíóúñü]{3,}/.test(l) && !/^\d+$/.test(l)) || "";

  return { amount, desc };
}

// Normaliza la orientación (y el tamaño) de la foto antes de pasarla al
// OCR. Es la causa más habitual de que un ticket se lea genial desde un
// iPhone y muy mal desde un Android: muchas cámaras de Android guardan el
// píxel "tumbado" y solo apuntan la rotación correcta en un dato EXIF
// aparte, mientras que el iPhone suele grabar el píxel ya girado del todo.
// Si no se corrige esa rotación antes de leer el texto, el OCR intenta
// leer letras de lado y la lectura empeora mucho. De paso se limita el
// tamaño máximo, porque muchas cámaras Android sacan fotos enormes
// (4000x3000 o más) que solo ralentizan el OCR sin mejorar la lectura.
export async function normalizeReceiptImage(file) {
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const MAX_DIM = 2000;
    const scale = Math.min(1, MAX_DIM / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.92));
    return blob || file;
  } catch (err) {
    // Si el navegador no soporta esto (poco probable, pero por si acaso),
    // se sigue con la imagen original en vez de romper el escaneo.
    console.warn("No se pudo normalizar la orientación del ticket, se usa la imagen original:", err);
    return file;
  }
}

// Ejecuta el OCR sobre una imagen (File/Blob) y devuelve el texto crudo más
// la mejor estimación de importe y descripción. Tesseract.js se importa de
// forma dinámica para no meterlo en el bundle principal de la app.
// También devuelve la imagen ya normalizada (orientación + tamaño
// corregidos) por si el usuario decide guardarla — así no hay que volver
// a procesarla otra vez para subirla.
export async function scanReceiptImage(file) {
  const normalized = await normalizeReceiptImage(file);
  const Tesseract = (await import("tesseract.js")).default;
  const { data } = await Tesseract.recognize(normalized, "spa");
  const text = data?.text || "";
  const { amount, desc } = parseReceiptText(text);
  return { text, amount, desc, blob: normalized };
}

// ── Guardado de adjuntos en Firebase Storage (función Pro) ─────────────
//
// Cada archivo se guarda en su propia carpeta privada ("receipts/<uid>/…"),
// con un nombre único generado aquí. Solo se guarda la RUTA del archivo en
// Firestore (nunca la URL pública) — la URL de descarga se pide al vuelo,
// solo cuando alguien quiere ver el archivo, para que el acceso pase
// siempre por las reglas de seguridad de Storage y no quede un enlace
// suelto que funcione para cualquiera que lo tenga.
//
// Requiere tener Cloud Storage activado en el proyecto de Firebase y unas
// reglas de seguridad tipo:
//
//   rules_version = '2';
//   service firebase.storage {
//     match /b/{bucket}/o {
//       match /receipts/{uid}/{fileName} {
//         allow read, write: if request.auth != null && request.auth.uid == uid;
//       }
//     }
//   }
//
// getStorage() usa la app de Firebase ya inicializada en el resto de la
// app (la misma que usan Firestore/Auth) — no hace falta tocar el archivo
// de configuración de Firebase para esto.
export async function uploadAttachment(file, uid) {
  const { getStorage, ref, uploadBytes } = await import("firebase/storage");
  const storage = getStorage();
  const isPdf = file.type === "application/pdf";
  const ext = isPdf ? "pdf" : "jpg";
  const path = `receipts/${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file, { contentType: file.type || "image/jpeg" });
  return path;
}

// Pide la URL de descarga de un adjunto ya guardado (solo funciona si las
// reglas de Storage dejan al usuario autenticado acceder a esa ruta).
export async function getAttachmentUrl(path) {
  const { getStorage, ref, getDownloadURL } = await import("firebase/storage");
  const storage = getStorage();
  return await getDownloadURL(ref(storage, path));
}

// Borra un adjunto de Storage. Se llama SIEMPRE que se borra de verdad la
// entrada/pendiente al que pertenecía — si no, la foto se queda huérfana
// en Storage para siempre, ocupando espacio (y sumando al coste) sin que
// nadie pueda volver a verla ni borrarla desde la app. No se llama cuando
// el registro solo se "transforma" (p.ej. un pendiente que se cobra y
// pasa a ser una entrada real, o una entrada que se convierte en
// pendiente) — ahí la referencia se traspasa al nuevo registro en vez de
// borrarse. Si el archivo ya no existe (o falla por lo que sea), no hace
// falta que rompa el borrado de la entrada — se registra el aviso y ya.
export async function deleteAttachment(path) {
  if (!path) return;
  try {
    const { getStorage, ref, deleteObject } = await import("firebase/storage");
    const storage = getStorage();
    await deleteObject(ref(storage, path));
  } catch (err) {
    console.warn("No se pudo borrar el adjunto de Storage:", path, err);
  }
}

// Borra TODOS los adjuntos de un usuario de golpe — pensado para cuando se
// borra la cuenta entera. En vez de ir entrada por entrada mirando su
// receiptPath (podría fallar o quedarse alguno suelto), se listan y borran
// directamente todos los archivos que haya bajo su carpeta privada
// "receipts/<uid>/" en Storage, así no puede quedar ninguno huérfano
// aunque algún registro de Firestore ya se hubiera borrado antes o el
// receiptPath no se hubiera guardado bien por algún motivo.
export async function deleteAllAttachments(uid) {
  if (!uid) return;
  try {
    const { getStorage, ref, listAll, deleteObject } = await import("firebase/storage");
    const storage = getStorage();
    const folderRef = ref(storage, `receipts/${uid}`);
    const { items } = await listAll(folderRef);
    await Promise.all(items.map(itemRef =>
      deleteObject(itemRef).catch(err =>
        console.warn("No se pudo borrar un adjunto al borrar la cuenta:", itemRef.fullPath, err)
      )
    ));
  } catch (err) {
    // Si la carpeta no existe (el usuario nunca guardó ningún adjunto),
    // listAll no falla — esto cubre otros errores (permisos, red, etc.)
    // sin bloquear el resto del borrado de la cuenta.
    console.warn("No se pudieron borrar los adjuntos del usuario al borrar la cuenta:", uid, err);
  }
}
